import { FC, useEffect, useMemo, useRef, useState } from "react";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Spin } from "../../../design-system";
import {
    AnalysisType,
    Project,
    ProjectAnalysis,
} from "../../../types/ProjectTypes";
import { getAnalysisOutputContent } from "../../../common/api/ProjectClient";
import { getRelativeTime } from "../../date/DateOp";
import { useAnalysisAssistant } from "../../aiAssistant/useAnalysisAssistant";
import { AskAssistantBubble } from "../../aiAssistant/AskAssistantBubble";
import { AttachmentsDropdown } from "./AttachmentsDropdown";
import { ReportTour } from "../tour/ReportTour";
import { ASK_ASSISTANT_TOUR_ID, getReportTourSteps } from "../tour/reportTourSteps";
import { hasSeenReportTour, markReportTourSeen } from "../tour/reportTourSeen";
import projectStyles from "../ProjectView.module.less";
import styles from "./FlexibleOutput.module.less";

interface FlexibleOutputProps {
    project: Project;
    analysis: ProjectAnalysis;
}

export const FlexibleOutput: FC<FlexibleOutputProps> = ({ project, analysis }) => {
    // No pre-loaded output: this view renders via an iframe, so the assistant
    // lazily fetches + serializes the output the first time the drawer opens.
    const { showAskButton, openAssistant, assistantDrawer } = useAnalysisAssistant({
        project,
        analysis,
    });

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [tourOpen, setTourOpen] = useState(false);
    const [reportHtml, setReportHtml] = useState<string | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);

    // Fetched and rendered via srcDoc rather than pointing the iframe at the
    // API: in prod the API is on api.infinityloop.ai, and a cross-origin
    // iframe would hide its contentDocument from the report tour.
    useEffect(() => {
        const controller = new AbortController();
        setReportHtml(null);
        setLoadFailed(false);
        getAnalysisOutputContent(project.id, analysis.version, { signal: controller.signal })
            .then(setReportHtml)
            .catch(() => {
                if (!controller.signal.aborted) {
                    setLoadFailed(true);
                }
            });
        return () => controller.abort();
    }, [project.id, analysis.version]);
    // The tour is written for deal/rfx report layouts; savings reports don't follow them.
    const tourSteps = useMemo(
        () =>
            analysis.type === AnalysisType.ROI
                ? null
                : getReportTourSteps(project.type, { includeAskAssistant: showAskButton }),
        [analysis.type, project.type, showAskButton]
    );

    const onIframeLoad = () => {
        if (tourSteps && !hasSeenReportTour(project.type)) {
            markReportTourSeen(project.type);
            setTourOpen(true);
        }
    };

    const titleText =
        analysis.type === AnalysisType.ROI
            ? "SAVINGS REPORT"
            : `NEGOTIATION PLAYBOOK V${analysis.version}`;

    const generatedDate =
        analysis.completedDate ?? analysis.submittedDate ?? analysis.recordCreationDate;

    return (
        <div className={`${projectStyles.tabContent} ${styles.container}`}>
            <div className={styles.header}>
                <span className={styles.title}>
                    {titleText}
                    <span className={styles.generated}>
                        {" · generated "}
                        {getRelativeTime(generatedDate)}
                    </span>
                </span>
                {tourSteps && (
                    <Button
                        type="text"
                        size="small"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setTourOpen(true)}
                    >
                        Take a tour
                    </Button>
                )}
                <AttachmentsDropdown
                    project={project}
                    analysis={analysis}
                    forceVisible={tourOpen}
                />
            </div>
            {loadFailed ? (
                <div className={styles.loading}>
                    <Alert
                        type="error"
                        message="The report failed to load. Refresh the page to try again."
                    />
                </div>
            ) : reportHtml === null ? (
                <div className={styles.loading}>
                    <Spin size="large" />
                </div>
            ) : (
                <iframe
                    ref={iframeRef}
                    srcDoc={reportHtml}
                    className={styles.iframe}
                    title="Report Content"
                    onLoad={onIframeLoad}
                />
            )}
            {tourSteps && (
                <ReportTour
                    steps={tourSteps}
                    open={tourOpen}
                    onClose={() => setTourOpen(false)}
                    iframeRef={iframeRef}
                />
            )}            
            {showAskButton && (
                <AskAssistantBubble onClick={openAssistant} tourId={ASK_ASSISTANT_TOUR_ID} />
            )}
            {assistantDrawer}
        </div>
    );
};
